import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

export default function TermsOfService() {
  const lastUpdated = 'January 24, 2026';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-900 via-surface-950 to-primary-950">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/register" 
            className="inline-flex items-center gap-2 text-surface-400 hover:text-primary-400 transition-colors mb-6"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Registration
          </Link>
          <h1 className="text-4xl font-display font-bold text-white mb-2">
            Terms of Service
          </h1>
          <p className="text-surface-400">Last updated: {lastUpdated}</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-lg max-w-none">
          <div className="bg-surface-900/80 backdrop-blur-xl rounded-2xl border border-surface-800 p-8 space-y-8">
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-surface-300 leading-relaxed">
                By accessing or using Neoshift BI ("Service"), operated by PT Neo Teknologi Perkasa ("Neoshift," "we," "us," or "our"), 
                you agree to be bound by these Terms of Service ("Terms"). If you are using the Service on behalf of an organization, 
                you represent that you have the authority to bind that organization to these Terms. If you do not agree to these Terms, 
                you may not use the Service.
              </p>
              <p className="text-surface-300 leading-relaxed mt-4">
                <strong>Legal Entity:</strong> PT Neo Teknologi Perkasa
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
              <p className="text-surface-300 leading-relaxed">
                Neoshift BI is a cloud-based business intelligence platform that provides data analytics, 
                visualization, dashboard creation, and AI-powered insights. The Service includes:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Data source connections and integrations</li>
                <li>Data warehousing and storage</li>
                <li>Dashboard and chart creation tools</li>
                <li>AI-powered analytics and natural language queries</li>
                <li>Collaboration and sharing features</li>
                <li>API access (where applicable based on your plan)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Account Registration</h2>
              <p className="text-surface-300 leading-relaxed">
                To use the Service, you must register for an account by providing accurate, complete, and current 
                information. You are responsible for:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized access</li>
                <li>Ensuring that your account information remains accurate and up-to-date</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Subscription Plans and Billing</h2>
              <p className="text-surface-300 leading-relaxed">
                The Service is offered under various subscription plans with different features and limitations. 
                By subscribing to a paid plan, you agree to:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Pay all applicable fees according to your selected plan</li>
                <li>Provide valid payment information</li>
                <li>Accept that subscriptions renew automatically unless cancelled</li>
              </ul>
              <p className="text-surface-300 leading-relaxed mt-4">
                We reserve the right to modify pricing with 30 days advance notice. Continued use of the Service 
                after price changes constitutes acceptance of the new pricing.
              </p>
              
              <h3 className="text-xl font-semibold text-white mt-6 mb-3">4.1 Refund Policy</h3>
              <p className="text-surface-300 leading-relaxed">
                We offer a 14-day money-back guarantee for all new subscriptions. If you are not satisfied with the Service 
                for any reason, you may cancel your subscription within 14 days of your initial purchase and receive a full refund. 
                To request a refund, please contact us at <span className="text-primary-400">support@bi.neoshift.ai</span> within the 
                14-day period.
              </p>
              <p className="text-surface-300 leading-relaxed mt-4">
                After the 14-day period, subscription fees are non-refundable. However, you may cancel your subscription at any time, 
                and you will retain access to the Service until the end of your current billing period. No refunds will be issued for 
                partial months or unused credits.
              </p>
              <p className="text-surface-300 leading-relaxed mt-4">
                Additional purchases such as credit packs, additional seats, or one-time add-ons are subject to the same 14-day 
                refund policy if purchased as part of a new subscription. For existing customers, these additional purchases are 
                non-refundable except as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. AI Credits and Usage</h2>
              <p className="text-surface-300 leading-relaxed">
                Certain AI-powered features consume credits. Your plan includes a specific allocation of credits 
                that may be monthly (resetting each billing cycle) or permanent (one-time allocation). 
                Additional credits may be purchased separately. Unused monthly credits do not roll over to the next period.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. AI-Generated Content and Recommendations</h2>
              <p className="text-surface-300 leading-relaxed">
                The Service includes AI-powered features such as the AI Assistant, natural language query processing, 
                and automated insights generation. You acknowledge and agree to the following regarding AI-generated content:
              </p>
              
              <h3 className="text-xl font-semibold text-white mt-6 mb-3">6.1 Accuracy and Limitations</h3>
              <p className="text-surface-300 leading-relaxed">
                AI-generated content, including but not limited to insights, recommendations, analyses, and suggestions, 
                may contain errors, inaccuracies, or incomplete information. Artificial intelligence models can:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Make mistakes or produce incorrect outputs</li>
                <li>Misinterpret data or context</li>
                <li>Generate recommendations that may not be suitable for your specific circumstances</li>
                <li>Provide outdated information or fail to account for recent changes</li>
                <li>Produce biased or incomplete analyses</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-white mt-6 mb-3">6.2 Not Professional Advice</h3>
              <p className="text-surface-300 leading-relaxed">
                AI-generated content is provided for informational purposes only and does not constitute professional 
                advice of any kind, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Financial, investment, or accounting advice</li>
                <li>Business, strategic, or operational consulting</li>
                <li>Legal, tax, or regulatory guidance</li>
                <li>Any other professional services</li>
              </ul>
              <p className="text-surface-300 leading-relaxed mt-4">
                You should not rely solely on AI-generated recommendations for making business decisions. Always consult 
                with qualified professionals appropriate to your specific situation before taking action based on AI insights.
              </p>
              
              <h3 className="text-xl font-semibold text-white mt-6 mb-3">6.3 Your Responsibility</h3>
              <p className="text-surface-300 leading-relaxed">
                You are solely responsible for:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Evaluating the accuracy and suitability of AI-generated content</li>
                <li>Verifying information before acting upon it</li>
                <li>Exercising your own independent judgment in all business decisions</li>
                <li>Any consequences resulting from decisions based on AI-generated content</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-white mt-6 mb-3">6.4 No Liability for AI Recommendations</h3>
              <p className="text-surface-300 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEOSHIFT DISCLAIMS ALL LIABILITY FOR ANY LOSSES, DAMAGES, 
                OR CONSEQUENCES ARISING FROM YOUR USE OF OR RELIANCE ON AI-GENERATED CONTENT, INCLUDING BUT NOT LIMITED TO 
                BUSINESS LOSSES, REVENUE IMPACTS, STRATEGIC DECISIONS, OR ANY OTHER DAMAGES. WE MAKE NO GUARANTEES 
                REGARDING THE ACCURACY, COMPLETENESS, RELIABILITY, OR SUITABILITY OF AI-GENERATED INSIGHTS FOR YOUR 
                PARTICULAR BUSINESS OR USE CASE.
              </p>
              <p className="text-surface-300 leading-relaxed mt-4">
                The AI features are tools to assist your analysis and decision-making, but the ultimate responsibility 
                for all business decisions remains with you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Acceptable Use</h2>
              <p className="text-surface-300 leading-relaxed">You agree not to:</p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Use the Service for any illegal purpose or in violation of any laws</li>
                <li>Upload or transmit malicious code, viruses, or harmful data</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Interfere with or disrupt the integrity or performance of the Service</li>
                <li>Reverse engineer, decompile, or disassemble the Service</li>
                <li>Use the Service to compete with or create a similar service</li>
                <li>Share account credentials or allow unauthorized access</li>
                <li>Exceed the usage limits of your subscription plan</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Your Data</h2>
              <p className="text-surface-300 leading-relaxed">
                You retain all rights to the data you upload to the Service ("Your Data"). By using the Service, 
                you grant us a limited license to process Your Data solely to provide and improve the Service. 
                We will not sell, share, or use Your Data for purposes other than providing the Service without 
                your consent.
              </p>
              <p className="text-surface-300 leading-relaxed mt-4">
                You are responsible for ensuring that you have the right to upload and process Your Data and that 
                doing so does not violate any third-party rights or applicable laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Data Security</h2>
              <p className="text-surface-300 leading-relaxed">
                We implement industry-standard security measures to protect Your Data, including:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security audits and monitoring</li>
                <li>Access controls and authentication</li>
                <li>Secure data centers with geographic redundancy</li>
              </ul>
              <p className="text-surface-300 leading-relaxed mt-4">
                However, no system is completely secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Intellectual Property</h2>
              <p className="text-surface-300 leading-relaxed">
                The Service, including its design, features, code, and documentation, is owned by Neoshift and 
                protected by intellectual property laws. You may not copy, modify, distribute, or create derivative 
                works based on the Service without our prior written consent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Third-Party Services</h2>
              <p className="text-surface-300 leading-relaxed">
                The Service may integrate with third-party services (such as data sources, APIs, and cloud providers). 
                Your use of these third-party services is subject to their respective terms and privacy policies. 
                We are not responsible for the actions or policies of third-party services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Service Availability</h2>
              <p className="text-surface-300 leading-relaxed">
                We strive to maintain high availability but do not guarantee uninterrupted access to the Service. 
                We may perform scheduled maintenance with advance notice when possible. We are not liable for any 
                downtime, data loss, or other issues resulting from factors beyond our reasonable control.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Limitation of Liability</h2>
              <p className="text-surface-300 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEOSHIFT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, 
                ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR 
                THE SERVICE IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">14. Disclaimer of Warranties</h2>
              <p className="text-surface-300 leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, 
                INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND 
                NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE OR UNINTERRUPTED.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">15. Termination</h2>
              <p className="text-surface-300 leading-relaxed">
                Either party may terminate this agreement at any time. Upon termination:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Your access to the Service will be suspended</li>
                <li>You may request export of Your Data within 30 days</li>
                <li>After 30 days, Your Data may be permanently deleted</li>
                <li>Outstanding fees remain payable</li>
              </ul>
              <p className="text-surface-300 leading-relaxed mt-4">
                We may suspend or terminate your account immediately if you violate these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">16. Changes to Terms</h2>
              <p className="text-surface-300 leading-relaxed">
                We may update these Terms from time to time. We will notify you of material changes via email or 
                through the Service. Your continued use of the Service after changes take effect constitutes 
                acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">17. Governing Law</h2>
              <p className="text-surface-300 leading-relaxed">
                These Terms are governed by the laws of the jurisdiction in which Neoshift is incorporated, 
                without regard to conflict of law principles. Any disputes shall be resolved through binding 
                arbitration or in the courts of that jurisdiction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">18. Contact Information</h2>
              <p className="text-surface-300 leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <p className="text-primary-400 mt-2">support@bi.neoshift.ai</p>
            </section>

          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-surface-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Neoshift BI. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
